import { useState, useEffect, useCallback, useRef } from "react";
import { useSequentialTimeouts, useSafeTimeout } from "../lib/hooks";

type Phase = "loading" | "animating" | "settling" | "gradient" | "done";

interface IntroAnimationProps {
  isLoading: boolean;
  onComplete: () => void;
}

// Кубическая кривая Безье для траектории
function cubicBezier(
  t: number,
  p0: number,
  p1: number,
  p2: number,
  p3: number,
): number {
  const mt = 1 - t;
  return (
    mt * mt * mt * p0 +
    3 * mt * mt * t * p1 +
    3 * mt * t * t * p2 +
    t * t * t * p3
  );
}

// Ease-in-out функция для плавности
function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

interface SunHeartProps {
  className?: string;
  style?: React.CSSProperties;
}

/**
 * SunHeart — сердце-солнце с лучами вокруг
 */
function SunHeart({ className = "", style }: SunHeartProps) {
  return (
    <div className={`sun-heart ${className}`} style={style}>
      {/* Лучи вокруг сердца */}
      <div className="sun-heart-rays" />
      {/* Само сердце */}
      <svg viewBox="0 0 48 48" className="sun-heart-svg">
        {/* Свечение */}
        <path
          d="M24 42c-1.5-1.5-4-3.5-7-6.5C11 30 4 23 4 15c0-6 5-11 11-11 3.5 0 6.5 1.5 9 4.5 2.5-3 5.5-4.5 9-4.5 6 0 11 5 11 11 0 8-7 15-13 20.5-3 3-5.5 5-7 6.5z"
          fill="#fcd34d"
          opacity="0.4"
          style={{ filter: "blur(6px)" }}
        />
        {/* Основное сердце */}
        <path
          d="M24 40c-1.2-1.2-3.5-3-6.5-5.8C11.5 29 5 22.5 5 15.5 5 10 9.5 5.5 15 5.5c3 0 5.8 1.3 8 3.8l1 1.2 1-1.2c2.2-2.5 5-3.8 8-3.8 5.5 0 10 4.5 10 10 0 7-6.5 13.5-12.5 18.7-3 2.8-5.3 4.6-6.5 5.8z"
          fill="#fbbf24"
        />
        {/* Блик */}
        <path
          d="M14 14c1.5-2 4-3 6-2"
          stroke="#fef3c7"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
          opacity="0.7"
        />
      </svg>
    </div>
  );
}

export function IntroAnimation({ isLoading, onComplete }: IntroAnimationProps) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [arcPosition, setArcPosition] = useState({
    x: 0,
    y: 0,
    scale: 1,
    opacity: 1,
    blur: 0,
  });
  const [showLogo, setShowLogo] = useState(false);
  const [logoMerge, setLogoMerge] = useState(false);
  const [logoPos, setLogoPos] = useState({ x: window.innerWidth / 2, y: 40 });

  const logoRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  /** Целевая позиция солнца (центр логотипа). Ref, чтобы анимация всегда читала актуальное значение без гонки с setState. */
  const logoPosRef = useRef<{ x: number; y: number }>({ x: window.innerWidth / 2, y: 40 });

  // Safe timeout for initial animation trigger
  const { setTimeout: safeSetTimeout } = useSafeTimeout();

  const ANIMATION_DURATION = 2200; // ms

  /**
   * Получает реальную позицию центра логотипа из DOM.
   * Если элемент ещё без размеров (картинка не загрузилась), используем левый верхний угол + половинный размер логотипа.
   */
  const getLogoCenter = useCallback((): { x: number; y: number } => {
    if (logoRef.current) {
      const rect = logoRef.current.getBoundingClientRect();
      const w = rect.width || 56;
      const h = rect.height || 56;
      return {
        x: rect.left + w / 2,
        y: rect.top + h / 2,
      };
    }
    // Fallback — логотип в header слева (как page-container + половинка логотипа), не центр экрана
    const paddingLeft = window.innerWidth >= 640 ? 28 : window.innerWidth >= 520 ? 24 : window.innerWidth >= 430 ? 20 : window.innerWidth >= 375 ? 16 : 10;
    const logoHalf = 32;
    return { x: paddingLeft + logoHalf, y: 40 };
  }, []);

  /**
   * Обновляет CSS переменные для позиции градиента фона
   */
  const updateGradientPosition = useCallback((x: number, y: number) => {
    const root = document.getElementById("root");
    if (root) {
      root.style.setProperty("--logo-x", `${x}px`);
      root.style.setProperty("--logo-y", `${y}px`);
    }
  }, []);

  // Обновляем позицию логотипа (state + ref) и CSS-переменные градиента.
  // Ref нужен, чтобы анимация солнца всегда читала актуальную цель без гонки с setState.
  const syncLogoPosition = useCallback(() => {
    const pos = getLogoCenter();
    logoPosRef.current = pos;
    setLogoPos(pos);
    updateGradientPosition(pos.x, pos.y);
  }, [getLogoCenter, updateGradientPosition]);

  // Устанавливаем позицию при монтировании и при resize (логотип уже в DOM — см. рендер ниже)
  useEffect(() => {
    syncLogoPosition();
    const handleResize = () => {
      requestAnimationFrame(syncLogoPosition);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [syncLogoPosition]);

  const animateArc = useCallback(
    (timestamp: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }

      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / ANIMATION_DURATION, 1);
      const t = easeInOutCubic(progress);

      // Центр экрана
      const startX = window.innerWidth / 2;
      const startY = window.innerHeight / 2;

      // Конечная точка — центр логотипа (из ref, чтобы не зависеть от тайминга setState)
      const { x: endX, y: endY } = logoPosRef.current;

      // Контрольные точки для красивой дуги (вправо-вверх, потом влево к логотипу)
      // Адаптируем под размер экрана
      const cp1X = Math.min(window.innerWidth * 0.75, startX + 150); // вправо, но не слишком далеко
      const cp1Y = Math.max(window.innerHeight * 0.15, endY + 50); // вверх
      const cp2X = Math.max(window.innerWidth * 0.2, endX + 50); // влево
      const cp2Y = endY; // на уровне логотипа

      // Вычисляем позицию по кубической кривой Безье
      const x = cubicBezier(t, startX, cp1X, cp2X, endX);
      const y = cubicBezier(t, startY, cp1Y, cp2Y, endY);
      const scale = 1 - t * 0.4; // от 1 до 0.6

      // Плавное затухание в последние 20% пути
      let opacity = 1;
      let blur = 0;
      if (progress > 0.8) {
        const fadeProgress = (progress - 0.8) / 0.2; // 0 to 1 in last 20%
        opacity = 1 - fadeProgress * 0.6; // от 1 до 0.4
        blur = fadeProgress * 4; // от 0 до 4px
      }

      setArcPosition({ x, y, scale, opacity, blur });

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animateArc);
      }
    },
    [],
  );

  // Sequential timeouts with automatic cleanup on unmount
  const runSequence = useSequentialTimeouts();

  const startAnimation = useCallback(() => {
    // Позиция логотипа уже в DOM (рендерим её и при loading). Обновляем ref и state перед стартом.
    syncLogoPosition();

    // Инициализируем начальную позицию
    setArcPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
      scale: 1,
      opacity: 1,
      blur: 0,
    });
    startTimeRef.current = 0;

    // Фаза 1: animating — сердце движется по дуге
    setPhase("animating");
    animationRef.current = requestAnimationFrame(animateArc);

    // Run all animation phase transitions with safe timeouts
    runSequence([
      // Показать логотип за 400ms до конца анимации (80% пути)
      {
        delay: ANIMATION_DURATION * 0.8,
        callback: () => setShowLogo(true),
      },
      // Через ANIMATION_DURATION — settling + эффект слияния + начало перехода к градиенту
      {
        delay: ANIMATION_DURATION,
        callback: () => {
          if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
          }
          const finalPos = logoPosRef.current;
          setArcPosition({
            x: finalPos.x,
            y: finalPos.y,
            scale: 0.6,
            opacity: 0,
            blur: 8,
          });
          setLogoMerge(true);
          setPhase("settling");
          // Градиент уже позиционирован корректно (CSS переменные установлены при монтировании)
          // Сразу начинаем показывать градиент фона — параллельно с затуханием белого
          document.getElementById("root")?.classList.add("intro-gradient");
        },
      },
      // Убрать эффект слияния через 300ms
      {
        delay: ANIMATION_DURATION + 300,
        callback: () => setLogoMerge(false),
      },
      // Через 400ms после settling — начинаем показывать контент (параллельно с градиентом)
      {
        delay: ANIMATION_DURATION + 400,
        callback: () => onComplete(), // Добавляет intro-complete → запускает каскадное появление
      },
      // Через 800ms — gradient фаза (белый фон затухает)
      {
        delay: ANIMATION_DURATION + 800,
        callback: () => setPhase("gradient"),
      },
      // Через 1200ms — done (интро полностью завершено)
      {
        delay: ANIMATION_DURATION + 1200,
        callback: () => setPhase("done"),
      },
    ]);
  }, [animateArc, onComplete, syncLogoPosition, runSequence]);

  useEffect(() => {
    if (!isLoading && phase === "loading") {
      safeSetTimeout(startAnimation, 100);
    }
  }, [isLoading, phase, startAnimation, safeSetTimeout]);

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  if (phase === "done") {
    return null;
  }

  // Стиль для анимируемого сердца
  const arcStyle: React.CSSProperties | undefined =
    phase === "animating"
      ? {
          position: "absolute",
          left: arcPosition.x,
          top: arcPosition.y,
          transform: `translate(-50%, -50%) scale(${arcPosition.scale})`,
          opacity: arcPosition.opacity,
          filter:
            arcPosition.blur > 0 ? `blur(${arcPosition.blur}px)` : undefined,
        }
      : undefined;

  // Всегда рендерим блок с логотипом (как в header), чтобы при phase === "loading"
  // он уже был в DOM и getLogoCenter() давал корректную позицию на любом экране.
  // При loading поверх показываем оверлей — пользователь видит только его.
  return (
    <>
    <div className="intro-animation">
      {/* Белый фон — начинает затухать на settling, параллельно с появлением градиента */}
      <div
        className={`intro-bg ${phase === "settling" || phase === "gradient" ? "intro-bg-fade" : ""}`}
      />

      {/* Логотип — та же структура, что и в AppHeader, для идентичной позиции на всех экранах */}
      <div className="intro-header safe-area-top">
        <div className="page-container py-2.5 xs:py-3 sm:py-4">
          <div
            ref={logoRef}
            className={`intro-logo ${showLogo ? "intro-logo-visible" : ""} ${logoMerge ? "intro-logo-merge" : ""}`}
          >
            <img src="/logo.png" alt="" className="w-14 h-14 xs:w-16 xs:h-16 md-mobile:w-[4.5rem] md-mobile:h-[4.5rem] lg-mobile:w-20 lg-mobile:h-20 rounded-xl md-mobile:rounded-2xl" />
          </div>
        </div>
      </div>

      {/* Сердце-солнце */}
      {phase === "animating" && (
        <SunHeart style={arcStyle} />
      )}

      {/* Лучи, которые расходятся — позиция через inline style */}
      <div
        className={`intro-rays ${phase === "settling" || phase === "gradient" ? "intro-rays-expand" : ""}`}
        style={{
          left: logoPos.x,
          top: logoPos.y,
        }}
      />
    </div>

    {/* Оверлей загрузки: поверх интро, пока грузим — солнце по центру */}
    {phase === "loading" && (
      <div
        className="fixed inset-0 z-[101] flex flex-col items-center justify-center bg-gradient-to-b from-amber-50/80 to-white/80 backdrop-blur-sm min-h-[100dvh]"
        style={{
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <SunHeart className="sun-heart-loading-overlay" />
      </div>
    )}
    </>
  );
}
